import React from 'react';

import { FlexBox } from 'ml-rubiks-styleguide/src/box';
import { Typography } from 'ml-rubiks-styleguide/src/typography';

import { toTelemetryContext } from '../../core/logs';
import { PaymentProcessor } from '../../core/payment-processor';
import { PaymentMethod } from '../../types';
import { AvailablePaymentMethodsV1Dto } from '../../types/api';
import {
    isFormBasedMethod,
    isProviderBasedMethod
} from '../../utils/payment-methods';
import { ErrorBoundaryWithContext } from '../common';
import { FormBasedPayment } from './form-based-payment';
import { ProviderBasedPayment } from './provider-based-payment';

interface PaymentWidgetCoreProps {
    methodType: PaymentMethod;
    paymentProcessor: PaymentProcessor;
    availablePaymentMethods?: AvailablePaymentMethodsV1Dto[];
    onBack?: () => void;
    onPaymentSuccess?: () => void;
}

export const PaymentWidgetCore: React.FC<PaymentWidgetCoreProps> = React.memo(
    ({
        methodType,
        paymentProcessor,
        availablePaymentMethods,
        onPaymentSuccess
    }) => {
        const config = paymentProcessor.getConfig();
        const hidePaymentButton = config?.hidePaymentButton;

        if (isFormBasedMethod(methodType)) {
            const availablePaymentMethod = availablePaymentMethods?.find(
                (availablePaymentMethod) =>
                    availablePaymentMethod.paymentProvider === 'NONE'
            );

            const paymentMethod = availablePaymentMethod?.paymentMethods?.find(
                (method) => method.paymentMethod === methodType
            );

            return (
                <FormBasedPayment
                    methodType={methodType}
                    paymentProcessor={paymentProcessor}
                    creditor={availablePaymentMethod?.creditor}
                    availablePaymentMethod={paymentMethod}
                    onPaymentSuccess={onPaymentSuccess}
                    hidePaymentButton={hidePaymentButton}
                />
            );
        }

        if (isProviderBasedMethod(methodType)) {
            return (
                <ProviderBasedPayment
                    methodType={methodType}
                    paymentProcessor={paymentProcessor}
                    availablePaymentMethods={availablePaymentMethods}
                    onPaymentSuccess={onPaymentSuccess}
                />
            );
        }

        // Fallback for uncategorized payment methods
        return (
            <FlexBox stack sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="error" sx={{ mb: 1 }}>
                    Unknown payment method: {methodType}
                </Typography>
                <Typography color="text.secondary">
                    This payment method is not configured in the system
                </Typography>
            </FlexBox>
        );
    }
);

PaymentWidgetCore.displayName = 'PaymentWidgetCore';

// Export error boundary wrapped version for JavaScript error handling.
// ErrorBoundaryWithContext (not raw ErrorBoundary) is required so caught
// errors still reach the payment error context and the host onError callback.
export const PaymentWidgetCoreWithErrorBoundary: React.FC<
    PaymentWidgetCoreProps
> = (props) => (
    <ErrorBoundaryWithContext
        telemetryContext={toTelemetryContext(
            props.paymentProcessor.getConfig()
        )}
    >
        <PaymentWidgetCore {...props} />
    </ErrorBoundaryWithContext>
);

PaymentWidgetCoreWithErrorBoundary.displayName =
    'PaymentWidgetCoreWithErrorBoundary';
